import { z } from 'zod';

/**
 * Registro de actividad (auditoría) y métricas de uso.
 * - `change`: cambios de la reunión (acuerdos, hojas, casos manuales, idea fuerza, juntas sensibles)
 * - `admin`: cambios de calibración y operación (umbrales, pesos, pantalla, foto, caché)
 * - `view`: uso (abrir la reunión, tesorería, un caso, exportar). Se agrupan por persona y objeto cada
 *   30 min para que los refrescos de la pantalla no inflen los números.
 */
export type ActivityCategory = 'change' | 'admin' | 'view';

export const ACTIVITY_ACTIONS = {
  'agreement.create': { label: 'Acuerdo registrado', category: 'change' },
  'agreement.update': { label: 'Acuerdo editado', category: 'change' },
  'agreement.delete': { label: 'Acuerdo eliminado', category: 'change' },
  'tandem_sheet.save': { label: 'Hoja de tándem guardada', category: 'change' },
  'tandem_sheet.delete': { label: 'Caso quitado de la hoja', category: 'change' },
  'manual_case.create': { label: 'Caso manual añadido', category: 'change' },
  'manual_case.update': { label: 'Caso manual editado', category: 'change' },
  'manual_case.close': { label: 'Caso manual cerrado', category: 'change' },
  'manual_case.reopen': { label: 'Caso manual reabierto', category: 'change' },
  'office.idea_fuerza': { label: 'Idea fuerza cambiada', category: 'change' },
  'meeting.sensitive': { label: 'Junta sensible', category: 'change' },
  'rules.update': { label: 'Umbrales cambiados', category: 'admin' },
  'scoring.save': { label: 'Pesos guardados', category: 'admin' },
  'display.save': { label: 'Pantalla configurada', category: 'admin' },
  'snapshot.regenerate': { label: 'Foto regenerada', category: 'admin' },
  'snapshot.auto': { label: 'Foto diaria (automática)', category: 'admin' },
  'cache.clear': { label: 'Caché vaciada', category: 'admin' },
  'access.update': { label: 'Roles con acceso cambiados', category: 'admin' },
  'board.view': { label: 'Abre la reunión', category: 'view' },
  'treasury.view': { label: 'Abre tesorería', category: 'view' },
  'case.view': { label: 'Abre un caso', category: 'view' },
  'agreements.export': { label: 'Exporta acuerdos', category: 'view' },
  'access.denied': { label: 'Acceso denegado', category: 'view' },
} as const satisfies Record<string, { label: string; category: ActivityCategory }>;

export type ActivityAction = keyof typeof ACTIVITY_ACTIONS;
export const ACTIVITY_ACTION_IDS = Object.keys(ACTIVITY_ACTIONS) as ActivityAction[];
export const ACTIVITY_CATEGORY_LABEL: Record<ActivityCategory, string> = {
  change: 'Cambios en la reunión',
  admin: 'Administración',
  view: 'Uso (consultas)',
};
export const activityLabel = (a: string) => ACTIVITY_ACTIONS[a as ActivityAction]?.label ?? a;

/** Texto normalizado para buscar: sin tildes ni diéresis (ñ → n), en minúsculas y con espacios simples. */
export const normalizeText = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** Actor del sistema (cron de la foto diaria) */
export const SYSTEM_ACTOR = { id: '00000000-0000-0000-0000-000000000000', email: 'sistema' } as const;

/** Un cambio de campo: antes → después */
export interface FieldChange {
  field: string;
  label: string;
  before: unknown;
  after: unknown;
}

export interface ActivityEntry {
  id: string;
  at: string;
  actorId: string;
  actorEmail: string;
  action: ActivityAction;
  category: ActivityCategory;
  officeId: string | null;
  officeName: string | null;
  entityType: string | null;
  entityId: string | null;
  /** Frase legible: «Acuerdo 42-03 · estado: ámbar → verde» */
  summary: string;
  /** Campos cambiados (antes/después) */
  changes: FieldChange[] | null;
  /** Contexto adicional (semana, caso, nº de veces que se abrió…) */
  details: Record<string, unknown> | null;
}

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const activityQuerySchema = z.object({
  q: z.string().max(200).optional(),
  actor: z.string().max(200).optional(),
  officeId: z.string().uuid().optional(),
  action: z.enum(ACTIVITY_ACTION_IDS as [ActivityAction, ...ActivityAction[]]).optional(),
  category: z.enum(['change', 'admin', 'view']).optional(),
  /** Día inicial y final incluidos (Europe/Madrid) */
  from: isoDay.optional(),
  to: isoDay.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ActivityQuery = z.infer<typeof activityQuerySchema>;

export interface ActivityPage {
  items: ActivityEntry[];
  total: number;
  /** Valores para los filtros (sobre todo el registro, no solo la página) */
  facets: {
    actors: { email: string; count: number }[];
    offices: { id: string; name: string; count: number }[];
  };
}

export const usageQuerySchema = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((d) => [7, 30, 90, 365].includes(d), 'Rango no válido')
    .default(30),
});

export interface UsageTotals {
  /** Personas distintas con alguna acción (excluido el sistema) */
  activeUsers: number;
  /** Oficinas con alguna acción */
  activeOffices: number;
  /** Oficinas que han trabajado la reunión: al menos un cambio (acuerdo, hoja, caso…) */
  workingOffices: number;
  views: number;
  changes: number;
  adminActions: number;
}

export interface UsageStats {
  days: number;
  from: string;
  to: string;
  /** Oficinas de la última foto (base de la adopción) */
  totalOffices: number;
  totals: UsageTotals;
  /** El mismo rango justo anterior, para comparar */
  previous: UsageTotals;
  daily: { date: string; views: number; changes: number; users: number }[];
  weekly: {
    week: string;
    activeUsers: number;
    activeOffices: number;
    workingOffices: number;
    agreements: number;
    sheets: number;
    manualCases: number;
  }[];
  offices: {
    officeId: string;
    officeName: string;
    users: number;
    views: number;
    changes: number;
    agreements: number;
    sheets: number;
    manualCases: number;
    /** Semanas del rango con algún cambio */
    activeWeeks: number;
    lastActivity: string | null;
  }[];
  users: {
    email: string;
    views: number;
    changes: number;
    adminActions: number;
    offices: number;
    firstSeen: string;
    lastSeen: string;
  }[];
  actions: { action: ActivityAction; count: number }[];
}
